// src/services/api.js
// Authenticated fetch wrapper for the handyman-backend admin routes.
//
// All requests include 'x-admin-secret' — the header that adminAuth.js
// in the backend checks. No Supabase JWTs, no user sessions — just the
// shared secret set in Railway's environment variables.
//
// The Content-Type header is only sent when a body is present. Sending
// it on body-less POSTs (like /approve) causes Express's json() parser
// to expect a body and throw a 400 error — this was bug #11 in the
// Task 9 testing log.

const BASE_URL = import.meta.env.VITE_API_URL;

// Read the currently-stored admin secret from localStorage directly.
// We don't use the AuthContext here (that's a React concern) — the
// service layer just reads storage and lets the caller handle auth errors.
const getSecret = () => localStorage.getItem('handyman_admin_secret') || '';

const request = async (method, path, body = null) => {
  const headers = {
    'x-admin-secret': getSecret(),
  };

  // Only set Content-Type when a body is being sent
  if (body !== null) {
    headers['Content-Type'] = 'application/json';
  }

  const config = {
    method,
    headers,
    ...(body !== null ? { body: JSON.stringify(body) } : {}),
  };

  const response = await fetch(`${BASE_URL}/api${path}`, config);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.error || `HTTP ${response.status}`);
    error.status = response.status;
    error.code = data.code;
    throw error;
  }

  return data;
};

/**
 * Check an admin secret against the backend before it is stored, so a wrong
 * secret never renders the dashboard. Resolves true/false; throws only on
 * network or server errors.
 * @param {string} secret
 */
export const verifyAdminSecret = async (secret) => {
  const response = await fetch(`${BASE_URL}/api/admin/handymen?status=pending`, {
    headers: { 'x-admin-secret': secret },
  });
  if (response.status === 401 || response.status === 403) return false;
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return true;
};

// ── Admin handyman operations ─────────────────────────────────────────────────

/**
 * Fetch all handymen with the given status.
 * @param {'pending'|'approved'|'rejected'} status
 */
export const fetchHandymen = (status) =>
  request('GET', `/admin/handymen?status=${status}`);

/**
 * Fetch full details for one handyman, including signed document URLs.
 * @param {string} id — handymen.id (UUID)
 */
export const fetchHandyman = (id) =>
  request('GET', `/admin/handymen/${id}`);

/**
 * Approve a handyman. No body required.
 * @param {string} id
 */
export const approveHandyman = (id) =>
  request('POST', `/admin/handymen/${id}/approve`);

/**
 * Reject a pending handyman with a mandatory reason. Final: their documents
 * are deleted (6b(c)). Calling it again on a rejected handyman retries only
 * the document clean-up (error code DOCUMENTS_NOT_REMOVED).
 * @param {string} id
 * @param {string} reason — at least 20 characters (validated server-side)
 */
export const rejectHandyman = (id, reason) =>
  request('POST', `/admin/handymen/${id}/reject`, { reason });

/**
 * Suspend an approved handyman (6b(c)). Resolves { affected: [...] }, the
 * customers whose bookings / quote requests were cancelled, to email.
 * Blockers come back as error.code (JOB_IN_PROGRESS, PENDING_PAYOUTS, …).
 * @param {string} id
 * @param {string} reason — admin-only, at least 10 characters
 */
export const suspendHandyman = (id, reason) =>
  request('POST', `/admin/handymen/${id}/suspend`, { reason });

/**
 * Reactivate a suspended handyman (6b(c)). No body required.
 * @param {string} id
 */
export const reactivateHandyman = (id) =>
  request('POST', `/admin/handymen/${id}/reactivate`);

// ── Admin payout operations (manual payout tracking, Option B) ─────────────

/**
 * Fetch every handyman with at least one 'pending' payout booking,
 * grouped with total owed and bank details.
 */
export const fetchPendingPayouts = () =>
  request('GET', '/admin/payouts');

/**
 * Mark specific bookings as paid for a handyman, once the manual bank
 * transfer has actually been sent outside the app.
 * @param {string} handymanId
 * @param {string[]} bookingIds
 */
export const markPayoutsPaid = (handymanId, bookingIds) =>
  request('POST', `/admin/payouts/${handymanId}/mark-paid`, { bookingIds });

/**
 * Fetch already-paid bookings (most recent first) for the founder's
 * own record-keeping/audit.
 */
export const fetchPayoutHistory = () =>
  request('GET', '/admin/payouts/history');

// ── Admin job type request operations ───────────────────────────────────────
// Handyman-proposed job types (submitted from MyJobPricesScreen) that aren't
// in the fixed menu yet, awaiting admin review.

/**
 * Fetch job type requests filtered by status.
 * @param {'pending'|'approved'|'rejected'|'all'} status
 */
export const fetchJobTypeRequests = (status) =>
  request('GET', `/admin/job-type-requests?status=${status}`);

/**
 * Approve a job type request with the admin-confirmed (possibly edited)
 * name/description. finalName is required server-side.
 * @param {string} id
 * @param {string} finalName
 * @param {string} finalDescription
 * @param {string|null} [categoryId] — only when moving the new job type to
 *   another of the 4 cross categories; null keeps the requested category.
 */
export const approveJobTypeRequest = (id, finalName, finalDescription, categoryId = null) =>
  request('POST', `/admin/job-type-requests/${id}/approve`, {
    finalName,
    finalDescription,
    ...(categoryId ? { categoryId } : {}),
  });

/**
 * Reject a job type request. reason is optional, but we always send a body
 * (even empty) so Content-Type is set and the backend's req.body.reason
 * read never hits an undefined req.body.
 * @param {string} id
 * @param {string} [reason]
 */
export const rejectJobTypeRequest = (id, reason) =>
  request('POST', `/admin/job-type-requests/${id}/reject`, { reason: reason || '' });

// ── Admin extra-category operations (item B) ────────────────────────────────
// A handyman declares extra categories in the app's Mis Precios. Revoking one
// switches off their prices in it (bookings are untouched); reinstating lets
// them offer it again once they re-enter their prices.

/**
 * Revoke a handyman's extra category. The reason (min 10 chars, validated
 * server-side) is sent to the handyman in a push notification.
 * @param {string} handymanId
 * @param {string} categoryId
 * @param {string} reason
 */
export const revokeExtraCategory = (handymanId, categoryId, reason) =>
  request('POST', `/admin/handymen/${handymanId}/extra-categories/${categoryId}/revoke`, { reason });

/**
 * Reinstate ("Restablecer") a revoked extra category. No body required.
 * @param {string} handymanId
 * @param {string} categoryId
 */
export const reinstateExtraCategory = (handymanId, categoryId) =>
  request('POST', `/admin/handymen/${handymanId}/extra-categories/${categoryId}/reinstate`);
